/**
 * PestConfigMobilePage — compact Pest Config editor for handheld devices
 * (Implementation Spec §4). Same conditional logic as the desktop master–detail
 * (shared via usePestConfigEditor) — only the layout differs.
 *
 * A horizontally-scrolling pest chip bar selects the pest; the body edits that
 * pest's PestConfig. Save persists the current pest via the pests API.
 *
 * Track: field_scout_pest_config_2b / Slice 2b.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  showsSamplingAndTally, SPEC_TYPES, SAMPLING_UNITS, TALLY_THRESHOLDS, CATEGORIES, TYPE_COLOR_KEY,
} from '../../scouting/pestConfig';
import { usePestConfigEditor } from '../../scouting/usePestConfigEditor';
import { PestFieldPreview } from './PestFieldPreview';
import './PestConfigMobilePage.css';

const PestConfigMobilePage: React.FC = () => {
  const { t } = useTranslation();
  const {
    pests, selectedId, selected, draft, descriptor, error, success, saving,
    select, changeType, patchDraft, adjustThreshold, resetDraft, save,
  } = usePestConfigEditor();

  return (
    <div className="pcm">
      <header className="pcm__head">
        <h1 className="pcm__title">{t('fieldScout.config.title')}</h1>
        <span className="pcm__sub">{t('fieldScout.config.subtitle', { count: pests.length })}</span>
      </header>

      {error && <div className="pcm__msg pcm__msg--err" role="alert">{error}</div>}
      {success && <div className="pcm__msg pcm__msg--ok" role="alert">{success}</div>}

      {/* horizontally-scrolling pest chip bar */}
      <div className="pcm__chipbar" role="list" aria-label={t('fieldScout.config.listLabel')}>
        {pests.map(p => (
          <button
            key={p._id}
            type="button"
            role="listitem"
            aria-current={p._id === selectedId ? 'true' : undefined}
            className={`pcm__chip${p._id === selectedId ? ' is-active' : ''}`}
            onClick={() => select(p)}
          >
            {p.code}
          </button>
        ))}
      </div>

      {draft && descriptor && selected && (
        <div className="pcm__body">
          <div className="pcm__pesttitle">
            <span className="pcm__code">{selected.code}</span>
            <span className="pcm__name">{selected.name}</span>
          </div>

          {/* measurement type */}
          <section className="pcm__field">
            <label className="pcm__label">{t('fieldScout.config.measurementType')}</label>
            <div className="pcm__seg" role="group" aria-label={t('fieldScout.config.measurementType')}>
              {SPEC_TYPES.map(ty => (
                <button
                  key={ty}
                  type="button"
                  aria-pressed={draft.measurementType === ty}
                  className={`pcm__seg-btn k-${TYPE_COLOR_KEY[ty]}${draft.measurementType === ty ? ' is-active' : ''}`}
                  onClick={() => changeType(ty)}
                >
                  {t(`fieldScout.config.type.${ty}`)}
                </button>
              ))}
            </div>
          </section>

          {/* conditional incidence-only block */}
          {showsSamplingAndTally(draft.measurementType) ? (
            <>
              <section className="pcm__field">
                <label className="pcm__label">{t('fieldScout.config.samplingUnit')}</label>
                <div className="pcm__chips" data-testid="field-sampling-unit">
                  {SAMPLING_UNITS.map(u => (
                    <button key={u} type="button" aria-pressed={draft.samplingUnit === u}
                      className={`pcm__chipopt${draft.samplingUnit === u ? ' is-active' : ''}`}
                      onClick={() => patchDraft({ samplingUnit: u })}>
                      {t(`fieldScout.config.sampling.${u}`)}
                    </button>
                  ))}
                </div>
              </section>
              <section className="pcm__field">
                <label className="pcm__label">{t('fieldScout.config.tallyThreshold')}</label>
                <div className="pcm__help">{t('fieldScout.config.tallyHelp')}</div>
                <div className="pcm__chips" data-testid="field-tally-threshold">
                  {TALLY_THRESHOLDS.map(tt => (
                    <button key={tt} type="button" aria-pressed={draft.tallyThreshold === tt}
                      className={`pcm__chipopt${draft.tallyThreshold === tt ? ' is-active' : ''}`}
                      onClick={() => patchDraft({ tallyThreshold: tt })}>
                      {t(`fieldScout.config.tally.${tt}`)}
                    </button>
                  ))}
                </div>
              </section>
            </>
          ) : (
            <div className="pcm__note">
              {t('fieldScout.config.nonIncidenceNote', { type: t(`fieldScout.config.type.${draft.measurementType}`) })}
            </div>
          )}

          {/* action threshold — units adapt to type */}
          <section className="pcm__field">
            <label className="pcm__label">{t('fieldScout.config.actionThreshold')}</label>
            <div className="pcm__stepperrow">
              <div className="pcm__stepper" role="group" aria-label={t('fieldScout.config.actionThreshold')}>
                <button type="button" aria-label={t('fieldScout.config.decrease')}
                  disabled={draft.actionThreshold <= descriptor.min}
                  onClick={() => adjustThreshold(-descriptor.step)}>−</button>
                <div className="pcm__stepper-v" data-testid="action-threshold-value">
                  {descriptor.render(draft.actionThreshold)}
                </div>
                <button type="button" aria-label={t('fieldScout.config.increase')}
                  disabled={draft.actionThreshold >= descriptor.max}
                  onClick={() => adjustThreshold(descriptor.step)}>+</button>
              </div>
              <span className="pcm__hint">{t(descriptor.unitLabelKey)}</span>
            </div>
          </section>

          {/* category */}
          <section className="pcm__field">
            <label className="pcm__label">{t('fieldScout.config.categoryLabel')}</label>
            <div className="pcm__chips">
              {CATEGORIES.map(c => (
                <button key={c} type="button" aria-pressed={draft.category === c}
                  className={`pcm__chipopt${draft.category === c ? ' is-active' : ''}`}
                  onClick={() => patchDraft({ category: c })}>
                  {t(`fieldScout.config.category.${c}`)}
                </button>
              ))}
            </div>
          </section>

          {/* live field preview */}
          <section className="pcm__field">
            <label className="pcm__label">{t('fieldScout.config.fieldPreview')}</label>
            <div className="pcm__help">{t('fieldScout.config.fieldPreviewHelp')}</div>
            <PestFieldPreview
              code={selected.code}
              name={selected.name}
              measurementType={draft.measurementType}
              actionThreshold={draft.actionThreshold}
            />
          </section>

          <div className="pcm__actions">
            <button type="button" className="pcm__btn pcm__btn--ghost" disabled={saving}
              onClick={resetDraft} data-testid="pcm-cancel">
              {t('fieldScout.config.cancel')}
            </button>
            <button type="button" className="pcm__btn pcm__btn--save" disabled={saving}
              onClick={save} data-testid="pcm-save">
              {t('fieldScout.config.savePest')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PestConfigMobilePage;
